import { useEffect, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { LocationForm } from './LocationForm';

type LocationOption = { id: string; name: string };

type AddDisplayModalProps = {
  organisationId: string;
  locations: LocationOption[];
  /** When set (e.g. from a location row), pre-select and lock that location. */
  preferredLocationId?: string | null;
  onClose: () => void;
  onLocationCreated: () => void;
  onScreenCreated: (screenId: string) => void;
};

/**
 * Modal to add a location (if needed) and a display — Escape / backdrop / Close dismisses.
 */
export function AddDisplayModal({
  organisationId,
  locations,
  preferredLocationId = null,
  onClose,
  onLocationCreated,
  onScreenCreated,
}: AddDisplayModalProps) {
  const { t } = useTranslation(['displays', 'common']);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const hasLocations = locations.length > 0;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-display-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
      style={{ background: 'color-mix(in srgb, #0a0a0b 55%, transparent)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-[14px] border border-line bg-panel p-5 shadow-lg sm:p-6"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 id="add-display-title" className="m-0 text-lg font-bold text-ink">
              {t('modal.addDisplay.title')}
            </h2>
            <p className="m-0 mt-1 text-sm text-muted">
              {!hasLocations
                ? t('modal.addDisplay.descriptionNoLocations')
                : preferredLocationId
                  ? t('modal.addDisplay.descriptionLocked')
                  : t('modal.addDisplay.descriptionPick')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-sm font-semibold text-muted hover:text-ink"
          >
            {t('common:actions.close')}
          </button>
        </div>

        <div className="space-y-4">
          {!hasLocations ? (
            <LocationForm organisationId={organisationId} onSaved={onLocationCreated} />
          ) : (
            <ScreenAndOptionalLocation
              organisationId={organisationId}
              locations={locations}
              preferredLocationId={preferredLocationId}
              onLocationCreated={onLocationCreated}
              onScreenCreated={onScreenCreated}
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ScreenAndOptionalLocation({
  organisationId,
  locations,
  preferredLocationId,
  onLocationCreated,
  onScreenCreated,
}: {
  organisationId: string;
  locations: LocationOption[];
  preferredLocationId: string | null;
  onLocationCreated: () => void;
  onScreenCreated: (screenId: string) => void;
}) {
  const { t } = useTranslation('displays');
  const [addingLocation, setAddingLocation] = useState(false);
  const locationLocked =
    Boolean(preferredLocationId) && locations.some((location) => location.id === preferredLocationId);

  if (addingLocation) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => setAddingLocation(false)}
          className="text-sm font-semibold text-muted hover:text-ink"
        >
          {t('modal.addDisplay.backToDisplay')}
        </button>
        <LocationForm
          organisationId={organisationId}
          onSaved={() => {
            onLocationCreated();
            setAddingLocation(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <ScreenForm
        organisationId={organisationId}
        locations={locations}
        preferredLocationId={locationLocked ? preferredLocationId : null}
        onCreated={onScreenCreated}
      />
      {!locationLocked ? (
        <button
          type="button"
          onClick={() => setAddingLocation(true)}
          className="w-full text-center text-sm font-semibold text-ink2 hover:text-ink"
        >
          {t('modal.addDisplay.needLocation')}
        </button>
      ) : null}
    </div>
  );
}

function resolveInitialLocationId(
  locations: LocationOption[],
  preferredLocationId: string | null,
): string {
  if (preferredLocationId && locations.some((location) => location.id === preferredLocationId)) {
    return preferredLocationId;
  }
  return locations[0]?.id ?? '';
}

function ScreenForm({
  organisationId,
  locations,
  preferredLocationId,
  onCreated,
}: {
  organisationId: string;
  locations: LocationOption[];
  preferredLocationId: string | null;
  onCreated: (screenId: string) => void;
}) {
  const { t } = useTranslation('displays');
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState(() =>
    resolveInitialLocationId(locations, preferredLocationId),
  );
  const lockedLocation = preferredLocationId
    ? locations.find((location) => location.id === preferredLocationId)
    : undefined;

  useEffect(() => {
    if (preferredLocationId && locations.some((location) => location.id === preferredLocationId)) {
      setLocationId(preferredLocationId);
      return;
    }
    if (!locations.some((location) => location.id === locationId)) {
      setLocationId(locations[0]?.id ?? '');
    }
  }, [locations, locationId, preferredLocationId]);

  const create = useMutation({
    mutationFn: () => api.createScreen(organisationId, { name: name.trim(), locationId }),
    onSuccess: (screen) => {
      setName('');
      onCreated(screen.id);
    },
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !locationId) return;
    create.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <label className="block text-sm font-medium text-ink">
        {t('modal.addDisplay.displayName')}
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('modal.addDisplay.displayNamePlaceholder')}
          autoFocus
          className="mt-1.5 w-full rounded-lg border border-line bg-field px-3 py-2.5 text-sm text-ink outline-none focus:border-[var(--gs-accent)]"
        />
      </label>
      {lockedLocation ? (
        <p className="m-0 rounded-lg border border-line bg-field px-3 py-2.5 text-sm text-ink">
          <span className="text-muted">{t('modal.addDisplay.location')} · </span>
          {lockedLocation.name}
        </p>
      ) : (
        <label className="block text-sm font-medium text-ink">
          {t('modal.addDisplay.location')}
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-line bg-field px-3 py-2.5 text-sm text-ink outline-none focus:border-[var(--gs-accent)]"
          >
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.name}
              </option>
            ))}
          </select>
        </label>
      )}
      {create.isError ? (
        <p className="m-0 text-sm text-bad" role="alert">
          {t('modal.addDisplay.createError')}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={create.isPending || !name.trim() || !locationId}
        className="w-full rounded-lg bg-signal px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
      >
        {create.isPending ? t('modal.addDisplay.adding') : t('modal.addDisplay.submit')}
      </button>
    </form>
  );
}
